import type { FileEntry } from '@/lib/api/generated/model';

/**
 * The empty file a new folder is made of.
 *
 * The workspace's storage has no directories, only key prefixes — a folder
 * exists exactly as long as something is stored under it. So a folder created
 * before anything is put in it needs a placeholder to hold the prefix open,
 * and that placeholder is never shown: the reader asked for a folder, not for
 * a file they did not write.
 *
 * Named for this app rather than `.keep`, which was the obvious choice and the
 * wrong one: `.keep` is a real convention, and several arrive in any checked-out
 * repository someone drops in. Hiding every file by that name would make a
 * reader's own file disappear from the listing with nothing to say why, and
 * a folder delete would count it as ours to remove quietly.
 */
export const FOLDER_PLACEHOLDER_NAME = '.product-x-next-folder';

export function isFolderPlaceholder(entry: FileEntry): boolean {
  return entry.kind === 'file' && entry.name === FOLDER_PLACEHOLDER_NAME;
}

/** The folder holding `path`; the empty string for the workspace root. */
export function parentOf(path: string): string {
  const cut = path.lastIndexOf('/');
  return cut === -1 ? '' : path.slice(0, cut);
}

/** The last segment of a path, for messages the reader will recognise. */
export function nameOf(path: string): string {
  const cut = path.lastIndexOf('/');
  return cut === -1 ? path : path.slice(cut + 1);
}

/**
 * Where `path` ends up once its last segment is `newName`.
 *
 * A rename is a move that holds the containing folder still, and the move
 * capability takes the full destination — so this is the one place the
 * "same folder, new name" rule is spelled out. The workspace root is the empty
 * path, which is why the separator is conditional: `/b.md` would be an absolute
 * path the service rejects.
 */
export function renamedPath(path: string, newName: string): string {
  const parent = parentOf(path);
  return parent === '' ? newName : `${parent}/${newName}`;
}

/**
 * How much of a file name to preselect when offering to rename it: the part
 * before the last extension, so typing replaces the name and keeps the
 * extension unless the reader deliberately changes it.
 *
 * "Before the last dot" rather than any guess at compound extensions —
 * `v1.2.txt` is a name with a dot in it, not a `.2.txt` file — and a leading
 * dot is part of the name, not an extension, so `.env` selects whole.
 */
export function stemLength(name: string): number {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? dot : name.length;
}

/** The root under which every task keeps its folder (service spec 113). */
export const TASKS_FOLDER = 'tasks';

/**
 * Why the product needs this folder to keep its exact name and place, or
 * `undefined` if it does not.
 *
 * - `.agents` (root only): the sandbox worker discovers agents by scanning
 *   `/workspace/.agents/*.md` (spec 063); renamed or moved, discovery switches
 *   off with nothing on the page to say why.
 * - `tasks`, and every folder directly inside it: each task finds its
 *   attachments and output by a path fixed when it was created (spec 120
 *   FR-010). A task folder that moved is a folder its task no longer knows.
 *
 * Refused with this reason for rename *and* move; deleting stays allowed —
 * a deleted task folder comes back empty when the task next needs it. Files
 * and folders deeper inside are ordinary.
 */
export function protectionReason(
  entry: Pick<FileEntry, 'kind' | 'path'>,
): string | undefined {
  if (entry.kind !== 'directory') return undefined;
  if (entry.path === '.agents') {
    return 'Agents are discovered in this folder by its name, so it cannot be renamed or moved.';
  }
  if (entry.path === TASKS_FOLDER) {
    return 'Tasks keep their files under this folder by its name, so it cannot be renamed or moved.';
  }
  const [root, name, ...deeper] = entry.path.split('/');
  if (root === TASKS_FOLDER && name && deeper.length === 0) {
    return "This is a task's folder. The task finds its attachments here by this exact path, so it cannot be renamed or moved.";
  }
  return undefined;
}

/** Whether the product relies on this folder keeping its name and place. */
export function isProtectedFolder(
  entry: Pick<FileEntry, 'kind' | 'path'>,
): boolean {
  return protectionReason(entry) !== undefined;
}

/**
 * Why the typed name cannot be used, or `undefined` if it can.
 *
 * One rule set for naming a new folder and for renaming anything: the name
 * becomes one path segment, so anything that would make it more than one — or
 * make it mean the folder itself, or its parent — is refused before a request
 * is sent rather than resolved into something the reader did not ask for.
 * `kind` only changes which word the messages use.
 */
export function validateEntryName(
  kind: FileEntry['kind'],
  name: string,
  existingNames: string[],
): string | undefined {
  const what = kind === 'directory' ? 'folder' : 'file';
  const trimmed = name.trim();
  if (trimmed === '') return `Enter a ${what} name.`;
  if (trimmed.includes('/') || trimmed.includes('\\')) {
    return `A ${what} name cannot contain “/” or “\\”.`;
  }
  if (trimmed === '.' || trimmed === '..') return 'Choose a different name.';
  if (existingNames.includes(trimmed))
    return `“${trimmed}” already exists here.`;
  return undefined;
}

/**
 * Every file a reader would say is inside `folderPath`, at any depth, in the
 * order the walk reaches them.
 *
 * Read to tell someone what deleting the folder will take with it, which is why
 * placeholders are left out: a folder made and never filled holds no files as
 * far as its author is concerned, and warning them about the file that holds
 * its prefix open shows them something they did not put there (FR-013a). The
 * placeholder is still deleted — the service removes the whole prefix in one
 * call, and never consults this list.
 *
 * Takes the lister as an argument so the walk is testable without a server, and
 * so the caller decides which listing it reads.
 */
export async function collectFolderFilePaths(
  list: (path: string) => Promise<FileEntry[]>,
  folderPath: string,
): Promise<string[]> {
  const entries = await list(folderPath);
  const paths = entries
    .filter((entry) => entry.kind === 'file' && !isFolderPlaceholder(entry))
    .map((entry) => entry.path);

  for (const entry of entries) {
    if (entry.kind === 'directory') {
      paths.push(...(await collectFolderFilePaths(list, entry.path)));
    }
  }
  return paths;
}

/**
 * Whether `path` names something stored under `folderPath`.
 *
 * Compares whole segments rather than characters: `docs2/readme.md` starts
 * with `docs`, and closing it because `docs` was deleted would be wrong.
 */
export function isInsideFolder(path: string, folderPath: string): boolean {
  return path.startsWith(`${folderPath}/`);
}

/**
 * Where a previewed `file` is after `from` moved to `to`, or `undefined` when
 * the move did not touch it and the selection should be left alone.
 *
 * Two cases follow: the file *is* the moved item, or it lies *inside* a moved
 * folder — in which case the folder's part of the path is swapped and the rest
 * kept. Containment is by whole segment, the same rule `isInsideFolder` uses
 * everywhere else: renaming `drafts` must not rewrite a preview of
 * `drafts2/x.md` (spec 103 FR-024).
 */
export function movedPreviewPath(
  file: string,
  from: string,
  to: string,
): string | undefined {
  if (file === from) return to;
  if (isInsideFolder(file, from)) return `${to}${file.slice(from.length)}`;
  return undefined;
}
